import React, { useState, useEffect } from 'react';
import { usePermissionContext } from '../contexts/PermissionContext';
import fetchApi from '../utils/api.js';
import PageSkeleton from '../components/ui/PageSkeleton';

import StatCard from '../components/ui/StatCard';
import { Icons } from '../components/ui/DashboardIcons';
import { MonthlyTrendsChart, FinancialMixPieChart, MonthlyBarChart } from '../components/ui/DashboardCharts';
import { SummaryTable, GstSummaryTable } from '../components/ui/DashboardTables';

const AccountingDashboard = () => {
    const { hasPermission } = usePermissionContext();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const data = await fetchApi('/accounting-dashboard/stats');
                setStats(data);
            } catch (error) {
                console.error("Accounting Stats Error:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (loading) return <PageSkeleton />;
    if (!stats) return <div style={{ padding: '20px' }}>Failed to load dashboard data.</div>;

    const {
        salesStats, purchaseStats, salesPayStats, purchasePayStats,
        purchaseReturnStats, salesReturnStats, expenseStats, chartData,
        truePurchasePayment, trueSalePayment,
        // New net accounting fields
        purchaseGrossAmount, purchaseReturnAmount, netPurchaseAmount,
        purchasePaymentAmount, purchaseOutstanding,
        salesGrossAmount, salesReturnAmount, netSalesAmount,
        salesPaymentAmount, salesOutstanding,
        grossProfit, netProfitLoss, gstDifference
    } = stats;

    // Fallback for older API responses (backward compatibility)
    const purOverall = purchaseStats.overall;
    const saleOverall = salesStats.overall;
    const expenseOverall = expenseStats.overall;

    const purGross    = purchaseGrossAmount  ?? purOverall.total;
    const purReturn   = purchaseReturnAmount ?? (purchaseReturnStats?.overall?.total || 0);
    const purNet      = netPurchaseAmount    ?? (purGross - purReturn);
    const purPaid     = purchasePaymentAmount ?? (truePurchasePayment ?? purchasePayStats.overall.total);
    const purOutst    = purchaseOutstanding  ?? (purNet - purPaid);

    const saleGross   = salesGrossAmount     ?? saleOverall.total;
    const saleReturn  = salesReturnAmount    ?? (salesReturnStats?.overall?.total || 0);
    const saleNet     = netSalesAmount       ?? (saleGross - saleReturn);
    const salePaid    = salesPaymentAmount   ?? (trueSalePayment ?? salesPayStats.overall.total);
    const saleOutst   = salesOutstanding     ?? (saleNet - salePaid);

    const grossPft    = grossProfit          ?? (saleNet - purNet);
    const netPL       = netProfitLoss        ?? (grossPft - expenseOverall.total);
    const gstDiff     = gstDifference        ?? (saleOverall.gst - purOverall.gst);

    // Mix Chart Data uses net values
    const mixData = [
        { name: 'Net Purchase', value: purNet, fill: '#f59e0b' },
        { name: 'Net Sales',    value: saleNet, fill: '#10b981' },
        { name: 'Expense',      value: expenseOverall.total, fill: '#ef4444' }
    ];
    const totalMix = mixData.reduce((s, d) => s + d.value, 0);

    return (
        <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', marginBottom: '24px' }}>Accounting Dashboard</h1>

            {/* ── PURCHASE SECTION ────────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '15px', marginBottom: '20px' }}>
                <StatCard title="Purchase Taxable Amount"  amount={purOverall.taxable} subtitle={`${purOverall.count} purchase invoices`} icon={Icons.doc}     colorClass="#f59e0b" bgColorClass="#fef3c7" textColorClass="#d97706" />
                <StatCard title="Purchase GST Amount"      amount={purOverall.gst}     subtitle="CGST + SGST + IGST"                       icon={Icons.receipt}  colorClass="#f59e0b" bgColorClass="#fef3c7" textColorClass="#d97706" />
                <StatCard title="Purchase Gross Amount"    amount={purGross}            subtitle="Total before returns"                     icon={Icons.cart}     colorClass="#f59e0b" bgColorClass="#fef3c7" textColorClass="#d97706" />
                <StatCard title="Purchase Return Amount"   amount={purReturn}           subtitle={`${purchaseReturnStats?.overall?.count || 0} returns`} icon={Icons.doc} colorClass="#ef4444" bgColorClass="#fee2e2" textColorClass="#ef4444" />
                <StatCard title="Net Purchase Amount"      amount={purNet}              subtitle="Gross − Returns"                          icon={Icons.balance}  colorClass="#f59e0b" bgColorClass="#fef3c7" textColorClass="#d97706" />
                <StatCard title="Purchase Payment Amount"  amount={purPaid}             subtitle="Total purchase payments"                  icon={Icons.payment}  colorClass="#ef4444" bgColorClass="#fee2e2" textColorClass="#ef4444" />
                <StatCard title="Purchase Outstanding"     amount={purOutst}            subtitle="Net Purchase − Paid"                      icon={Icons.balance}  colorClass="#f59e0b" bgColorClass="#fef3c7" textColorClass="#d97706" isNegative={purOutst < 0} />
            </div>

            {/* ── SALES SECTION ────────────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '15px', marginBottom: '20px' }}>
                <StatCard title="Sales Taxable Amount"     amount={saleOverall.taxable} subtitle={`${saleOverall.count} sale invoices`}    icon={Icons.doc}     colorClass="#0891b2" bgColorClass="#cffafe" textColorClass="#0891b2" />
                <StatCard title="Sales GST Amount"         amount={saleOverall.gst}     subtitle="CGST + SGST + IGST"                       icon={Icons.receipt}  colorClass="#0891b2" bgColorClass="#cffafe" textColorClass="#0891b2" />
                <StatCard title="Sales Gross Amount"       amount={saleGross}           subtitle="Total before returns"                     icon={Icons.cart}     colorClass="#0891b2" bgColorClass="#cffafe" textColorClass="#0891b2" />
                <StatCard title="Sales Return Amount"      amount={saleReturn}          subtitle={`${salesReturnStats?.overall?.count || 0} returns`} icon={Icons.doc} colorClass="#ef4444" bgColorClass="#fee2e2" textColorClass="#ef4444" />
                <StatCard title="Net Sales Amount"         amount={saleNet}             subtitle="Gross − Returns"                          icon={Icons.balance}  colorClass="#0891b2" bgColorClass="#cffafe" textColorClass="#0891b2" />
                <StatCard title="Sales Payment Amount"     amount={salePaid}            subtitle="Total sale receipts"                      icon={Icons.payment}  colorClass="#10b981" bgColorClass="#d1fae5" textColorClass="#10b981" />
                <StatCard title="Sales Outstanding"        amount={saleOutst}           subtitle="Net Sales − Received"                     icon={Icons.balance}  colorClass="#0891b2" bgColorClass="#cffafe" textColorClass="#0891b2" isNegative={saleOutst < 0} />
            </div>

            {/* ── ACCOUNTING SECTION ──────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '24px' }}>
                <StatCard title="Total Expense"  amount={expenseOverall.total}        subtitle={`${expenseOverall.count} total expenses`}   icon={Icons.doc}        colorClass="#64748b" bgColorClass="#f1f5f9" textColorClass="#64748b" />
                <StatCard title="Gross Profit"   amount={Math.abs(grossPft)}          subtitle="Net Sales − Net Purchase"                   icon={Icons.balance}    colorClass={grossPft >= 0 ? "#10b981" : "#ef4444"} bgColorClass={grossPft >= 0 ? "#d1fae5" : "#fee2e2"} textColorClass={grossPft >= 0 ? "#10b981" : "#ef4444"} isNegative={grossPft < 0} />
                <StatCard title="Net Profit / Loss" amount={Math.abs(netPL)}          subtitle="Gross Profit − Expenses"                    icon={Icons.calculator} colorClass={netPL >= 0 ? "#10b981" : "#ef4444"} bgColorClass={netPL >= 0 ? "#d1fae5" : "#fee2e2"} textColorClass={netPL >= 0 ? "#10b981" : "#ef4444"} isNegative={netPL < 0} />
                <StatCard title="GST Difference" amount={Math.abs(gstDiff)}           subtitle="Output GST − Input GST"                     icon={Icons.receipt}    colorClass="#8b5cf6" bgColorClass="#ede9fe" textColorClass="#8b5cf6" isNegative={gstDiff < 0} />
            </div>

            {/* Charts Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '24px' }}>
                <MonthlyTrendsChart chartData={chartData} />
                <FinancialMixPieChart mixData={mixData} totalMix={totalMix} />
            </div>

            {/* Bar Chart Row */}
            <MonthlyBarChart chartData={chartData} />

            {/* Tables Row */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <SummaryTable stats={stats} />
                <GstSummaryTable stats={stats} />
            </div>

        </div>
    );
};

export default AccountingDashboard;
